// Drafts for the copilot's report forms. A half-written bug report is the one form whose loss is
// silent — someone who can't get a report to us can't tell us reporting is broken — so every
// keystroke is mirrored to localStorage and the form refills itself after a close, reload or
// navigation. Attachments are not kept (too big for storage); the words are.
import { BUG_SEVERITIES, type BugSeverity } from "@gmx/shared/support-files";

export type DraftKind = "bug" | "feature" | "feedback" | "support";
export interface SupportDraft {
  title: string;
  description: string;
  severity: BugSeverity;
  savedAt: number;
}

export const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;
export const EMPTY_DRAFT: SupportDraft = { title: "", description: "", severity: "normal", savedAt: 0 };
export const draftKey = (kind: DraftKind, userId: string) => `gf-support-draft:${userId}:${kind}`;

const isEmpty = (d: Pick<SupportDraft, "title" | "description">) => !d.title.trim() && !d.description.trim();

/** Anything that isn't a fresh draft we wrote (junk, expired, empty) reads as null. */
export function parseDraft(raw: string | null, at = Date.now()): SupportDraft | null {
  if (!raw) return null;
  let v: unknown;
  try {
    v = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  const savedAt = typeof r.savedAt === "number" ? r.savedAt : 0;
  if (!savedAt || at - savedAt > DRAFT_TTL_MS) return null;
  const draft: SupportDraft = {
    title: typeof r.title === "string" ? r.title.slice(0, 160) : "",
    description: typeof r.description === "string" ? r.description.slice(0, 12000) : "",
    severity: (BUG_SEVERITIES as readonly unknown[]).includes(r.severity) ? (r.severity as BugSeverity) : "normal",
    savedAt,
  };
  return isEmpty(draft) ? null : draft;
}

/** A restore only ever fills an EMPTY field — it can never overwrite what someone is typing. */
export function applyDraft(current: SupportDraft, stored: SupportDraft | null): SupportDraft {
  if (!stored) return current;
  return {
    title: current.title.trim() ? current.title : stored.title,
    description: current.description.trim() ? current.description : stored.description,
    severity: current.severity !== "normal" ? current.severity : stored.severity,
    savedAt: Math.max(current.savedAt, stored.savedAt),
  };
}

// Storage can be blocked (private mode, policies); a draft is a convenience and must never break a render.
export function readDraft(kind: DraftKind, userId: string): SupportDraft | null {
  try {
    return parseDraft(localStorage.getItem(draftKey(kind, userId)));
  } catch {
    return null;
  }
}
export function saveDraft(kind: DraftKind, userId: string, draft: Omit<SupportDraft, "savedAt">): void {
  try {
    if (isEmpty(draft)) localStorage.removeItem(draftKey(kind, userId));
    else localStorage.setItem(draftKey(kind, userId), JSON.stringify({ ...draft, savedAt: Date.now() }));
  } catch {
    /* nothing here is worth failing a keystroke over */
  }
}
export function clearDraft(kind: DraftKind, userId: string): void {
  try {
    localStorage.removeItem(draftKey(kind, userId));
  } catch {
    /* see above */
  }
}
